import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { Variants } from 'framer-motion'
import { motion, useAnimation } from 'framer-motion'
import { BulkReviewButton } from '@/ui/components/items/bulk-review-button'
import { ExportItemsButton } from '@/ui/components/items/export-items-button'
import { ItemsFilters } from '@/ui/components/items/items-filters'
import { ItemsTabs } from '@/ui/components/items/items-tabs'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'

const ItemsPage = () => {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'items'
  })

  return (
    <div className="h-full">
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <div className="min-h-full bg-neutral-100/10 p-4">
        <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <PackageIcon />
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('items.title')}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('items.subtitle')}
            </h2>
          </header>
          <div className="flex items-center gap-4">
            <BulkReviewButton />
            <ExportItemsButton />
          </div>
        </div>
        <ItemsFilters />
        <ItemsTabs />
      </div>
    </div>
  )
}

export default ItemsPage

const packageVariants: Variants = {
  normal: { scale: 1 },
  animate: {
    scale: 1.1,
    y: [0, -5, 0],
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
      y: { repeat: 1, delay: 0.1, duration: 0.4 }
    }
  }
}

export const PackageIcon = () => {
  const controls = useAnimation()
  const isControlledRef = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('animate')
    }
  }, [controls])

  const handleMouseLeave = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('normal')
    }
  }, [controls])

  return (
    <div
      className="flex select-none items-center justify-center overflow-hidden rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm transition-colors duration-200"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={22}
        height={22}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-neutral-700"
        variants={packageVariants}
        animate={controls}
        transition={{ duration: 0.2 }}
      >
        <path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5"></path>
        <path d="M12 12l8 -4.5"></path>
        <path d="M12 12l0 9"></path>
        <path d="M12 12l-8 -4.5"></path>
        <path d="M16 5.25l-8 4.5"></path>
      </motion.svg>
    </div>
  )
}
